import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Download, FilePlus, Loader2, RefreshCw } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { formatCents } from "../../../../lib/sales";
import { canMakeFile, canRemakeFile, getCardOrder, isPaidOrder, type CardOrderDetail, type CardOrderRow } from "../../../../lib/cardOrders";
import { Btn, Modal, fmtBytes, fmtDateTime } from "../../../ui";
import { CardOrderStatus } from "./CardOrderRow";

/** One labelled fact, as the import settings draw theirs. */
function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="crm-lab !mt-0">{label}</span>
      <div className="min-w-0 break-words text-[13px] font-semibold">{children}</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mt-5 first:mt-0">
      <div className="crm-card-t mb-2.5">{title}</div>
      <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">{children}</div>
    </section>
  );
}

/**
 * One order: who it is from, what the cards say, what was paid, and the print file.
 *
 * Opened from a row, which already carries everything but the cards; the texts come from
 * the order's own route, so the list never has to read the designs it shows.
 *
 * A kept file can be made again (`onRemake`): the list asks first and does it, because the
 * question is a window of its own and two windows do not sit on each other here. A paid
 * order that keeps no file has nothing to download, so its main button makes one
 * (`onMake` opens the studio page that draws it).
 */
export function CardOrderDialog({
  order,
  downloading,
  making,
  onDownload,
  onRemake,
  onMake,
  onClose,
}: {
  order: CardOrderRow;
  downloading: boolean;
  making: boolean;
  onDownload: (order: CardOrderRow) => void;
  onRemake: (order: CardOrderRow) => void;
  onMake: (order: CardOrderRow) => void;
  onClose: () => void;
}) {
  const t = useT();
  const [detail, setDetail] = useState<CardOrderDetail | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    setDetail(null);
    setFailed(false);
    getCardOrder(order.id)
      .then((d) => live && setDetail(d))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [order.id]);

  const billing = order.billing;
  const paid = isPaidOrder(order);

  return (
    <Modal
      title={t("sales.cardOrders.dialogTitle", { code: order.code })}
      label={t("sales.cardOrders.dialogTitle", { code: order.code })}
      headExtra={<CardOrderStatus order={order} />}
      onClose={onClose}
      footer={
        <>
          <Btn onClick={onClose}>{t("common.close")}</Btn>
          {canMakeFile(order) ? (
            <Btn tone="dark" onClick={() => onMake(order)} disabled={making}>
              {making ? <Loader2 className="size-4 animate-spin" /> : <FilePlus className="size-4" />}
              {t("sales.cardOrders.makeFile")}
            </Btn>
          ) : (
            <Btn tone="dark" onClick={() => onDownload(order)} disabled={!order.hasFile || downloading}>
              {downloading ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
              {t("sales.cardOrders.download")}
            </Btn>
          )}
        </>
      }
    >
      <div className="max-h-[62vh] overflow-y-auto px-1 pb-1">
        {order.test ? <p className="crm-mut mb-4 text-[13px] leading-relaxed">{t("sales.cardOrders.testNote")}</p> : null}
        {!paid ? <p className="crm-mut mb-4 text-[13px] leading-relaxed">{t("sales.cardOrders.unpaidBody")}</p> : null}
        {paid && !order.hasFile ? <p className="crm-mut mb-4 text-[13px] leading-relaxed">{t("sales.cardOrders.noFileBody")}</p> : null}

        <Section title={t("sales.cardOrders.sectionBuyer")}>
          <Fact label={t("sales.cardOrders.billName")}>{billing?.name || "-"}</Fact>
          <Fact label={t("sales.cardOrders.email")}>
            <a href={`mailto:${order.email}`} className="hover:underline">
              {order.email}
            </a>
          </Fact>
          {billing?.regCode ? <Fact label={t("sales.cardOrders.regCode")}>{billing.regCode}</Fact> : null}
          {billing?.vatNumber ? <Fact label={t("sales.cardOrders.vatNumber")}>{billing.vatNumber}</Fact> : null}
          {billing?.address ? <Fact label={t("sales.cardOrders.address")}>{billing.address}</Fact> : null}
        </Section>

        <Section title={t("sales.cardOrders.sectionCards")}>
          <Fact label={t("sales.cardOrders.design")}>{order.design || "-"}</Fact>
          {detail?.cards.company ? <Fact label={t("sales.cardOrders.cardCompany")}>{detail.cards.company}</Fact> : null}
          <div className="sm:col-span-2">
            <Fact label={t("sales.cardOrders.people", { n: order.names })}>
              {detail ? (
                <ol className="mt-0.5 flex flex-col gap-1">
                  {detail.cards.people.map((p, i) => (
                    <li key={i} className="flex min-w-0 gap-2">
                      <span className="crm-mut crm-tnum w-6 flex-none text-right font-normal">{i + 1}.</span>
                      <span className="min-w-0">
                        {p.name || <span className="crm-mut font-normal">{t("sales.cardOrders.noName")}</span>}
                        {p.role ? <span className="crm-mut font-normal"> · {p.role}</span> : null}
                      </span>
                    </li>
                  ))}
                </ol>
              ) : failed ? (
                <span className="font-normal text-red-600">{t("sales.cardOrders.loadFailed")}</span>
              ) : (
                <Loader2 className="size-4 animate-spin text-muted-foreground" />
              )}
            </Fact>
          </div>
        </Section>

        <Section title={t("sales.cardOrders.sectionPayment")}>
          <Fact label={t("sales.cardOrders.amount")}>
            <span className="crm-tnum">{formatCents(order.amountCents, order.currency)}</span>
          </Fact>
          <Fact label={t("sales.cardOrders.ordered")}>
            <span className="crm-tnum">{fmtDateTime(order.createdAt)}</span>
          </Fact>
          {order.paidAt ? (
            <Fact label={t("sales.cardOrders.paidAt")}>
              <span className="crm-tnum">{fmtDateTime(order.paidAt)}</span>
            </Fact>
          ) : null}
          {order.invoiceId ? (
            <Fact label={t("sales.cardOrders.invoice")}>
              <Link to={`/sales/invoices/${order.invoiceId}`} className="crm-tnum hover:underline">
                {order.invoiceNumber || t("sales.cardOrders.openInvoice")}
              </Link>
            </Fact>
          ) : null}
          {detail?.clientId ? (
            <Fact label={t("sales.cardOrders.client")}>
              <Link to={`/clients/${detail.clientId}`} className="hover:underline">
                {billing?.name || order.email}
              </Link>
            </Fact>
          ) : null}
          {order.hasFile ? (
            <div className="flex min-w-0 flex-col items-start gap-2.5 sm:col-span-2">
              <Fact label={t("sales.cardOrders.file")}>
                <span className="break-all">{order.fileName}</span>
                <span className="crm-mut font-normal"> · {fmtBytes(order.fileBytes)}</span>
              </Fact>
              {canRemakeFile(order) ? (
                <Btn small onClick={() => onRemake(order)}>
                  <RefreshCw className="size-4" />
                  {t("sales.cardOrders.remake")}
                </Btn>
              ) : null}
            </div>
          ) : null}
        </Section>
      </div>
    </Modal>
  );
}
